import { useState } from "react";
import { ActivityIndicator } from "react-native";
import { Redirect, useRouter } from "expo-router";

import { AuthScreen } from "~/components/composed/AuthScreen";
import {
  AppText,
  Button,
  ButtonText,
  CenteredScreen,
  SegmentedControl,
  Stack,
  TextField,
} from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { useKitActions } from "~/state/kits";
import { fetchKits } from "~/state/sync";
import {
  HOME_PATH,
  isInitialOnboardingComplete,
  withOnboardingComplete,
} from "~/utils/onboarding";

export default function OnboardingScreen() {
  const router = useRouter();
  const {
    session,
    user,
    loading,
    updateProfile,
    updatePreferences,
    updatePreferencesPending,
    updateProfilePending,
    refresh,
  } = useAuth();
  const kitActions = useKitActions();

  const [name, setName] = useState("Workshop");
  const [units, setUnits] = useState<"metric" | "imperial">(
    user?.units === "imperial" ? "imperial" : "metric",
  );
  const [error, setError] = useState<string | null>(null);

  if (loading) {
    return (
      <CenteredScreen>
        <ActivityIndicator />
      </CenteredScreen>
    );
  }

  if (!session?.user) {
    return <Redirect href="/login" />;
  }

  if (isInitialOnboardingComplete(user)) {
    return <Redirect href={HOME_PATH as never} />;
  }

  const finish = async (rename: boolean) => {
    setError(null);
    try {
      const trimmed = name.trim();
      if (rename && trimmed) {
        // The starter kit is provisioned server-side right at sign-up, so
        // the cached kits list from useKits() can still be empty here --
        // fetch it fresh rather than risk silently skipping the rename.
        const [starterKit] = await fetchKits();
        if (starterKit) {
          await kitActions.updateAsync(starterKit.id, { name: trimmed });
        }
      }
      await updateProfile({ units });
      await updatePreferences(withOnboardingComplete(user?.preferences));
      await refresh();
      router.replace(HOME_PATH as never);
    } catch (finishError) {
      setError(
        finishError instanceof Error
          ? finishError.message
          : "Could not finish setting up.",
      );
    }
  };

  const busy = updatePreferencesPending || updateProfilePending;

  return (
    <AuthScreen
      title="Welcome to GearSync"
      subtitle="Name your first kit, then start cataloguing."
    >
      <Stack gap="md">
        {error ? (
          <AppText tone="danger" size="xs">
            {error}
          </AppText>
        ) : null}

        <Stack gap="xs">
          <AppText size="xs" weight="medium">
            What should we call your first kit?
          </AppText>
          <TextField
            accessibilityLabel="First kit name"
            value={name}
            onChangeText={setName}
            placeholder="Workshop, van kit, camera bag..."
          />
          <AppText size="xs">
            Kits group gear however it is stored — a bag, a van, a bench, or a
            job. You can add as many as you like.
          </AppText>
        </Stack>

        <Stack gap="xs">
          <AppText size="xs" weight="medium">
            Measurements
          </AppText>
          <SegmentedControl
            value={units}
            options={[
              { label: "Metric (mm)", value: "metric" },
              { label: "Imperial (in)", value: "imperial" },
            ]}
            onChange={(value) => setUnits(value as "metric" | "imperial")}
          />
        </Stack>

        <Button disabled={busy} onPress={() => void finish(true)}>
          <ButtonText>{busy ? "Saving..." : "Start cataloguing"}</ButtonText>
        </Button>
        <Button
          variant="outline"
          disabled={busy}
          onPress={() => void finish(false)}
        >
          <ButtonText>Skip for now</ButtonText>
        </Button>
      </Stack>
    </AuthScreen>
  );
}
